import React, { useState, useMemo } from 'react';
import { SEO } from './SEO';
import '../styles/skills.css';  // We can reuse the same CSS

interface Support {
  name: string;
  type: string;
  tags: string;
  description: string;
  effects: string[];
}

export const Supports: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 21;
  const supports = require('../data/all_supports.json') as Support[];

  // Filter supports based on search term
  const filteredSupports = useMemo(() => {
    const searchLower = searchTerm.toLowerCase();
    return supports.filter(support => 
      (support.name?.toLowerCase() || '').includes(searchLower) ||
      (support.type?.toLowerCase() || '').includes(searchLower) ||
      (support.tags?.toLowerCase() || '').includes(searchLower) ||
      (support.description?.toLowerCase() || '').includes(searchLower)
    );
  }, [supports, searchTerm]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredSupports.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSupports = filteredSupports.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <SEO 
        title="POE2 Support Gems"
        description="Complete database of Path of Exile 2 support gems. Find detailed information about support gem mechanics and effects."
        canonicalUrl="https://www.poe2skills-ai.com/supports"
      />
      <div className="skills-container">
        <h1>PoE2 Support Gems</h1>
        
        <div className="search-container">
          <input
            type="text"
            placeholder="Search support gems by name, type, or tags..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1); // Reset to first page on search
            }}
            className="search-input"
          />
        </div>

        <div className="skills-grid">
          {currentSupports.length > 0 ? (
            currentSupports.map((support, index) => (
              <div key={`${support.name}-${index}`} className="skill-card">
                <div className="skill-type">{support.type}</div>
                <h2>{support.name}</h2>
                <p className="skill-description">{support.description}</p>
                <div className="skill-tags">
                  {support.tags.split(', ').filter(tag => tag).map((tag, tagIndex) => (
                    <span key={`${tag}-${tagIndex}`} className="skill-tag">{tag}</span>
                  ))}
                </div>
                <div className="skill-meta">
                  {support.effects.map((effect, effectIndex) => (
                    <div key={`${effect}-${effectIndex}`} className="skill-effects">
                      {effect}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="no-results">
              No support gems found matching "{searchTerm}"
            </div>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <button 
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            
            {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className={`pagination-button ${pageNum === currentPage ? 'active' : ''}`}
              >
                {pageNum}
              </button>
            ))}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </>
  );
}; 